<script setup>
import { ref, nextTick, computed, watch } from 'vue';
import { useRoute, createError, navigateTo, showError } from '#app';
import { useApiSlugs } from '@/composables/useApiSlugs';
import { useCmsPage } from '@/composables/useCmsPage';
import { useBlocks } from '@/composables/useBlocks';
import { useSeo } from '@/composables/useSeo';
import { useUiStore, mapHeaderClass } from '@/stores/ui';

import AosContainer from '@/components/AosContainer.vue';
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue';
import BlockHeroArticle from '@/components/blocks/BlockHeroArticle.vue';
import ShareButtons from '@/components/ui/ShareButtons.vue';

const route = useRoute();
const ui = useUiStore();
const blockComponents = useBlocks();
const { getPageSlug } = useApiSlugs();

const pageType = 'promo-article';

const apiSlug = computed(() => getPageSlug());

const { data } = await useCmsPage(apiSlug);
const isRedirect = computed(() => !!data.value?.redirect?.redirect_url);
const content = computed(() => (!isRedirect.value ? data.value?.data : null));
const seoData = computed(() => {
  const pageData = content.value;
  if (!pageData) return null;

  return {
    title: pageData.seo_title || pageData.title || '',
    description: pageData.meta_description || '',
    keywords: pageData.meta_keywords || '',
    robots: pageData.meta_robots || 'index, follow',
    image: pageData.image_cover?.src || null,
    path: pageData.url || '',
  };
});

useSeo({
  title: computed(() => seoData.value?.title),
  description: computed(() => seoData.value?.description),
  keywords: computed(() => seoData.value?.keywords),
  robots: computed(() => seoData.value?.robots),
  image: computed(() => seoData.value?.image),
  path: computed(() => seoData.value?.path),
});

watch(
  data,
  (newData) => {
    if (!newData?.success || isRedirect.value || !newData.data) return;
    ui.setHeaderSkin(mapHeaderClass(newData.data.background_color));
  },
  { immediate: true },
);

const blockRefs = ref({});
const setBlockRef = (id) => (componentInstance) => {
  if (componentInstance) {
    if (componentInstance.$el) {
      blockRefs.value[id] = componentInstance.$el;
    } else if (componentInstance instanceof HTMLElement) {
      blockRefs.value[id] = componentInstance;
    } else {
      nextTick(() => {
        const element = document.querySelector(`[data-block-id="${id}"]`);
        if (element) blockRefs.value[id] = element;
      });
    }
  } else {
    delete blockRefs.value[id];
  }
};

const articleBlocks = computed(() => {
  return (
    content.value?.sections?.filter(
      (block) => block.data?.template === 'article',
    ) || []
  );
});

const otherBlocks = computed(() => {
  return (
    content.value?.sections?.filter(
      (block) => block.data?.template !== 'article',
    ) || []
  );
});
</script>

<template>
  <AosContainer
    v-if="data?.success && !isRedirect"
    :options="{ once: true, duration: 600, easing: 'ease-out' }"
  >
    <div v-if="data?.success" class="page-dynamic">
      <Breadcrumbs
        v-if="content?.breadcrumbs?.items"
        :breadcrumbs="content?.breadcrumbs?.items"
      />

      <BlockHeroArticle
        :data="{
          title: content?.title,
          intro: content?.intro,
          image: content?.thumbnails,
          duration: content?.duration,
        }"
      />

      <div v-if="articleBlocks.length" class="article">
        <article class="article__wrap">
          <component
            :is="blockComponents[block.type]"
            v-for="block in articleBlocks"
            :id="block.id"
            :key="block.id"
            :ref="setBlockRef(block.id)"
            :data="block.data"
            :page-type="pageType"
            :path="content?.url"
          />
          <ShareButtons
            class="article__share"
            :url="content?.url"
            :view="'light'"
          />
        </article>
      </div>

      <template v-for="block in otherBlocks" :key="block.id">
        <component
          :is="blockComponents[block.type]"
          :id="block.id"
          :ref="setBlockRef(block.id)"
          :data="block.data"
          :page-type="pageType"
          :path="content?.url"
        />
      </template>
    </div>
  </AosContainer>
</template>

<style scoped lang="scss">
.article {
  padding: 0 16px 80px;
  @include media-breakpoint-up(sm) {
    padding: 0 20px 120px;
  }
  @include media-breakpoint-up(md) {
    padding: 0 48px 120px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(padding-left, 48);
    @include responsive-spacing(padding-right, 48);
    @include responsive-spacing(padding-bottom, 120);
  }
  &__wrap {
    background-color: $white;
    border-radius: 8px;
    padding-bottom: 24px;
    position: relative;
    @include media-breakpoint-up(sm) {
      padding-bottom: 40px;
    }
    @include media-breakpoint-up(md) {
      min-height: 474px;
      padding-bottom: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(min-height, 474);
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(padding-bottom, 80);
    }
  }
  &__share {
    margin: 0 auto;
    width: fit-content;
    @include media-breakpoint-up(sm) {
      margin-top: 16px;
    }
    @include media-breakpoint-up(md) {
      margin-top: 0;
      position: absolute;
      top: 80px;
      left: 40px;
      z-index: 2;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(top, 80);
      @include responsive-spacing(left, 40);
    }
  }
}
.error-container {
  min-height: calc(100vh - 450px);
  @include media-breakpoint-up(md) {
    min-height: calc(100vh - 450px);
  }
  @include media-breakpoint-up(xl) {
    min-height: calc(100vh - px-to-vw(450));
  }
}
</style>
